import {axios} from "../../..";

export function LinkGoogleAccount() {
  initiateGoogleAuth("link");
}

export async function UnlinkGoogleAccount(userEmail: string) {
  try {
    const response = await unlinkGoogleAccountWithCredentials(userEmail);

    if (response.status === "success") {
      // Dispatch success event to Settings page
      const successEvent = new CustomEvent("googleUnlinkSuccess", {
        detail: { message: "Google account successfully unlinked!" },
      });
      window.dispatchEvent(new CustomEvent("setSuccessMessage", {
        detail: { message: "Google account successfully unlinked!" },
      }));
      window.dispatchEvent(successEvent);
      return response;
    } else {
      throw new Error(response.message || "Failed to unlink account");
    }
  } catch (error: any) {
    // Dispatch error event to Settings page
    const errorEvent = new CustomEvent("googleUnlinkError", {
      detail: { message: error.message || "Failed to unlink Google account" },
    });
    window.dispatchEvent(errorEvent);
    throw error;
  }
}

export function LoginWithGoogle() {
  initiateGoogleAuth("login");
}

async function initiateGoogleAuth(action: "link" | "login") {
  try {
    const currentUrl = window.location.origin;
    const redirectUri = `${currentUrl}/auth/google/callback`;

    // Store the action in sessionStorage to retrieve after callback
    sessionStorage.setItem("googleAuthAction", action);

    const url =
      action === "link"
        ? "/google-sso"
        : "/google-sso?action=initiateGoogleSSOLogin";

    const response = await axios.Post(url, {
      redirectUri,
    });

    if (response.data && response.data.authUrl) {
      // Open popup for Google authentication
      const popup = window.open(
        response.data.authUrl,
        "googleAuth",
        "width=700,height=800,scrollbars=yes,resizable=yes,top=0,left=0,same-origin-allow-popups"
      );

      // Listen for popup completion
      const checkClosed = setInterval(() => {
        if (popup?.closed) {
          clearInterval(checkClosed);
          // Refresh page or update state as needed
          window.location.reload();
        }
      }, 1000);

      // Handle popup message - use a single event listener to avoid duplicates
      const handleMessage = async (event: MessageEvent) => {
        if (event.origin !== window.location.origin) {
          return;
        }

        if (event.data.type === "GOOGLE_AUTH_ACCOUNT_ALREADY_LINKED") {
          popup?.close();
          clearInterval(checkClosed);
          window.dispatchEvent(new CustomEvent("setErrorMessage", {
            detail: { message: event.data.data.message },
          }));
          return;
        }
        if (event.data.type === "GOOGLE_AUTH_NOT_LINKED") {
          popup?.close();
          clearInterval(checkClosed);
          window.removeEventListener("message", handleMessage);
          showLinkRequiredMessage();
          return;
        }

        if (event.data.type === "GOOGLE_AUTH_SUCCESS") {
          clearInterval(checkClosed);
          window.removeEventListener("message", handleMessage);

          // Don't close popup yet if this is a linking action - wait for linking to complete
          if (action === "link") {
            handleAuthSuccess(event.data, action)
              .then(() => {
                popup?.close();
              })
              .catch((error) => {
                console.error("error", error);
                popup?.close();
              });
          } else {
            popup?.close();
            await handleAuthSuccess(event.data, action);
          }
        } else if (event.data.type === "GOOGLE_AUTH_ERROR") {
          popup?.close();
          clearInterval(checkClosed);
          window.removeEventListener("message", handleMessage);

          window.dispatchEvent(
            new CustomEvent("googleLoginError", {
              detail: {
                message:
                  typeof event.data.error === "string"
                    ? event.data.error
                    : event.data.error?.message ||
                      "Google login failed. Please try again.",
                code: event.data.code,
              },
            }),
          );
        }
      };

      window.addEventListener("message", handleMessage);
    }
  } catch (error: any) {
    console.error("Error initiating Google authentication:", error);
  }
}

async function handleAuthSuccess(data: any, action: "link" | "login") {
  if (action === "login") {
    if (data.action === "login") {
      // Complete Google login process - dispatch event to LoginForm
      const tokens = data.data.tokens || {};

      const loginEvent = new CustomEvent("googleLoginSuccess", {
        detail: {
          tokens: tokens,
          user: data.user,
          message: "B2C Google SSO login successful",
        },
      });
      window.dispatchEvent(loginEvent);
    } else if (data.action === "link_required") {
      // Show message to user to log in with regular credentials
      showLinkRequiredMessage();
    }
  } else if (action === "link") {
    if (data.action === "link_required") {
      await handleAccountLinking(
        data.data.googleAccountId,
        data.data.googleEmail
      );
    }
  }
}

async function handleAccountLinking(
  googleAccountId: string,
  googleEmail: string
) {
  try {
    // Get the current user's email from the auth context
    const currentUserId = getCurrentUserId();

    if (!currentUserId) {
      throw new Error("Unable to get current user email");
    }

    const response = await linkGoogleAccountWithCredentials(
      currentUserId,
      googleAccountId,
      googleEmail
    );

    if (response.status === "success") {
      // Dispatch success event to Settings page
      const successEvent = new CustomEvent("googleLinkSuccess", {
        detail: {
          message: "Google account successfully linked!",
          googleAccountId,
        },
      });
      window.dispatchEvent(new CustomEvent("setSuccessMessage", {
        detail: { message: "Google account successfully linked!" },
      }));
      window.dispatchEvent(successEvent);
    } else {
      throw new Error(response.message || "Failed to link account");
    }
  } catch (error: any) {
    // Dispatch error event to Settings page
    const errorEvent = new CustomEvent("googleLinkError", {
      detail: { message: error.message || "Failed to link Google account" },
    });
    window.dispatchEvent(errorEvent);

    throw error;
  }
}

function getCurrentUserId(): string | null {
  // Try to get from sessionStorage or cookies
  let tokenDecoded = sessionStorage.getItem("token_decoded");

  // If not in sessionStorage, try cookies
  if (!tokenDecoded) {
    const getCookie = (name: string): string | null => {
      const value = `; ${document.cookie}`;
      const parts = value.split(`; ${name}=`);
      if (parts.length === 2) {
        return parts.pop()?.split(";").shift() || null;
      }
      return null;
    };
    tokenDecoded = getCookie("token_decoded");
  }

  if (tokenDecoded) {
    try {
      const decoded = JSON.parse(tokenDecoded);

      // Try different email fields that might be available in the JWT
      return (
        decoded.oid ||
        decoded.sub ||
        null
            );
    } catch (e) {
      // Error parsing token
    }
  }

  return null;
}

function showLinkRequiredMessage() {
  // This will be implemented in the LoginForm component
  const event = new CustomEvent("socialAccountLinkingRequired", {
    detail: {
      message:
        "Please log in with your regular credentials to link this Google account.",
      socialProvider: "google",
    },
  });
  window.dispatchEvent(event);
}

export async function linkGoogleAccountWithCredentials(
  username: string,
  googleAccountId: string,
  googleEmail: string
) {
  try {
    const response = await axios.Post("/google-sso?action=link", {
      username,
      googleAccountId,
      googleEmail,
    });

    return response.data;
  } catch (error) {
    console.error("Error linking Google account:", error);
    throw error;
  }
}

export async function unlinkGoogleAccountWithCredentials(userId: string) {
  try {
    const response = await axios.Post("/google-sso?action=unlink", {
      userId,
    });

    return response.data;
  } catch (error) {
    console.error("Error unlinking Google account:", error);
    throw error;
  }
}

export async function getGoogleLinkStatus(username: string) {
  try {
    const response = await axios.Get(
      `/google-sso?action=status&username=${username}`
    );
    return response.data;
  } catch (error) {
    console.error("Error getting Google link status:", error);
    throw error;
  }
}
